import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Card, CardActionArea, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { formatUsdMoney } from '../../utils/animatedNumber';

type DashboardAnalyticsSummaryStatCardProps = {
    icon: React.ReactNode;
    title: string;
    value: string;
    inlineComparisonValue?: string;
    inlineComparisonDelta?: number;
    delta?: number;
    deltaPercent?: number;
    deltaLabel?: string;
    progressCurrent?: number;
    progressMax?: number;
    selected?: boolean;
    onClick?: () => void;
};

const DashboardAnalyticsSummaryStatCard = ({
    icon,
    title,
    value,
    inlineComparisonValue,
    inlineComparisonDelta,
    delta,
    deltaPercent,
    deltaLabel = 'vs Previous Period',
    progressCurrent = 0,
    progressMax = 0,
    selected = false,
    onClick,
}: DashboardAnalyticsSummaryStatCardProps) => {
    const safeMax = Math.max(0, progressMax);
    const safeCurrent = Math.min(Math.max(0, progressCurrent), safeMax || progressCurrent);
    const showProgress = safeMax > 0 && safeCurrent < safeMax;
    const progressValue = safeMax > 0 ? (safeCurrent / safeMax) * 100 : 0;
    const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
    const hasDeltaPercent = typeof deltaPercent === 'number' && Number.isFinite(deltaPercent);
    const isPositive = (delta || 0) >= 0;
    const deltaColor = isPositive ? 'success.main' : 'error.main';
    const hasInlineComparisonDelta = typeof inlineComparisonDelta === 'number' && Number.isFinite(inlineComparisonDelta);
    const isInlineComparisonPositive = (inlineComparisonDelta || 0) >= 0;
    const inlineComparisonColor = isInlineComparisonPositive ? 'success.main' : 'error.main';

    return (
        <Card
            elevation={selected ? 8 : 2}
            sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                borderWidth: selected ? 2 : 1,
                borderStyle: 'solid',
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 2,
            }}
        >
            <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        {icon}
                        <Typography variant="body1" color="text.secondary">
                            {title}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            {value}
                        </Typography>
                        {inlineComparisonValue ? (
                            hasInlineComparisonDelta ? (
                                <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: inlineComparisonColor }}>
                                    {isInlineComparisonPositive ? (
                                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                    ) : (
                                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                    )}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                        {inlineComparisonValue}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {inlineComparisonValue}
                                </Typography>
                            )
                        ) : null}
                    </Stack>
                    {hasDelta && (
                        <>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: deltaColor }}>
                                {isPositive ? <ArrowUpwardIcon sx={{ fontSize: 18 }} /> : <ArrowDownwardIcon sx={{ fontSize: 18 }} />}
                                {hasDeltaPercent ? (
                                    <Typography variant="h6" fontWeight={700} sx={{ pr: 1.5 }}>
                                        {Math.abs(deltaPercent || 0).toFixed(1)}%
                                    </Typography>
                                ) : null}
                                <Typography variant="h6" fontWeight={700}>
                                    {formatUsdMoney(Math.abs(delta || 0))}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {deltaLabel}
                            </Typography>
                        </>
                    )}
                </CardContent>
            </CardActionArea>
            {showProgress ? (
                <LinearProgress
                    variant="determinate"
                    value={progressValue}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 3,
                    }}
                />
            ) : null}
        </Card>
    );
};

export default DashboardAnalyticsSummaryStatCard;
